import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Slider,
  Box,
} from '@mui/material';
import { FilterList, Clear } from '@mui/icons-material';

export interface PartnerFilters {
  serviceType?: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance' | '';
  status?: 'active' | 'inactive' | 'suspended' | '';
  minRating?: number;
  search?: string;
}

interface PartnerFilterDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: PartnerFilters) => void;
  initialFilters?: PartnerFilters;
}

export const PartnerFilterDialog: React.FC<PartnerFilterDialogProps> = ({
  open,
  onClose,
  onApply,
  initialFilters = {},
}) => {
  const [filters, setFilters] = useState<PartnerFilters>(initialFilters);

  useEffect(() => {
    if (open) {
      setFilters(initialFilters);
    }
  }, [open, initialFilters]);

  const handleChange = (field: keyof PartnerFilters, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClear = () => {
    const clearedFilters: PartnerFilters = {
      serviceType: '',
      status: '',
      minRating: 0,
      search: '',
    };
    setFilters(clearedFilters);
    onApply(clearedFilters);
    onClose();
  };

  const handleApply = () => {
    // Remove empty filters
    const activeFilters: PartnerFilters = {};
    if (filters.serviceType) activeFilters.serviceType = filters.serviceType;
    if (filters.status) activeFilters.status = filters.status;
    if (filters.minRating && filters.minRating > 0) activeFilters.minRating = filters.minRating;
    if (filters.search) activeFilters.search = filters.search;

    onApply(activeFilters);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Filter Partners
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Search by name"
              value={filters.search || ''}
              onChange={(e) => handleChange('search', e.target.value)}
              placeholder="Enter partner name..."
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Service Type</InputLabel>
              <Select
                value={filters.serviceType || ''}
                label="Service Type"
                onChange={(e) => handleChange('serviceType', e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="Inspection">Inspection</MenuItem>
                <MenuItem value="GPS">GPS Installation</MenuItem>
                <MenuItem value="Documentation">Documentation</MenuItem>
                <MenuItem value="Insurance">Insurance Verification</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                label="Status"
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom>
              Minimum Trust Rating
            </Typography>
            <Box sx={{ px: 1 }}>
              <Slider
                value={filters.minRating || 0}
                onChange={(_, value) => handleChange('minRating', value)}
                valueLabelDisplay="auto"
                step={0.5}
                marks
                min={0}
                max={5}
                valueLabelFormat={(value) => `${value}/5`}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Current: {filters.minRating || 0}/5
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClear} startIcon={<Clear />} color="inherit">
          Clear All
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleApply} startIcon={<FilterList />}>
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};
